<script setup lang="ts">
/**
 * Every household in a group, and what to do about each one (SPEC §8.10, §6.5).
 *
 * The column that matters is whether a family is connected. An organizer
 * looking at twelve of these wants to know which are not yet reachable, and
 * counting contacts by eye is how one gets missed until the Sunday nobody
 * replies — so connection is a word and a colour rather than a number to
 * compare.
 *
 * A table on a desktop and cards on a phone, from one list. Two renderings of
 * the same data rather than two data paths, so neither can show something the
 * other does not.
 */
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useRoute } from 'vue-router'
import { ApiError, endpoints, type HouseholdResponse } from '@/api'
import EmptyState from '@/components/EmptyState.vue'

const route = useRoute()
const display = useDisplay()

const groupId = computed(() => route.params.groupId as string)
const isMobile = computed(() => display.smAndDown.value)

const households = ref<HouseholdResponse[]>([])
const includeArchived = ref(false)
const loading = ref(true)
const busy = ref<string | null>(null)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)

/** A link is fetched when somebody asks for it, never for every row. */
const copiedLinkFor = ref<string | null>(null)

/*
  Adding a household.

  The API has had `POST /groups/{id}/households` since MT-063 and nothing ever
  called it, so a group's household list was a screen that could only ever be
  empty. The empty state told people to add the first one and then offered no
  way to do it.

  Headcounts default to 2 adults and 0 children, which is what the API defaults
  to, and both are editable here rather than only on the detail screen — an
  organizer entering eight families knows the sizes as they type the names, and
  making them come back for it is eight more trips.
*/
const creating = ref(false)
const newName = ref('')
const newAdults = ref(2)
const newChildren = ref(0)
const newNotes = ref('')
const nameError = ref<string | null>(null)

function openCreate(): void {
  newName.value = ''
  newAdults.value = 2
  newChildren.value = 0
  newNotes.value = ''
  nameError.value = null
  creating.value = true
}

async function create(): Promise<void> {
  const name = newName.value.trim()

  if (!name) {
    nameError.value = 'Give the household a name.'
    return
  }

  busy.value = 'create'
  error.value = null
  nameError.value = null

  try {
    const household = await endpoints.createHousehold(groupId.value, {
      name,
      defaultAdultCount: newAdults.value,
      defaultChildCount: newChildren.value,
      foodNotes: newNotes.value.trim() || null,
    })

    creating.value = false

    // Prepended rather than refetched. The list is already correct and a
    // reload would lose the archived filter and scroll position for one row.
    households.value = [household, ...households.value]
    notice.value = `${household.name} added. Send them an invitation when you are ready.`
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

const unconnected = computed(() =>
  households.value.filter((h) => h.status === 'Active' && !h.isConnected),
)

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

async function load() {
  loading.value = true
  error.value = null

  try {
    households.value = await endpoints.listHouseholds(groupId.value, includeArchived.value)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

async function copyLink(household: HouseholdResponse) {
  busy.value = household.id
  error.value = null

  try {
    const link = await endpoints.householdLink(groupId.value, household.id)

    await navigator.clipboard.writeText(link.url)
    copiedLinkFor.value = household.id
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

async function setArchived(household: HouseholdResponse, archived: boolean) {
  busy.value = household.id
  error.value = null
  notice.value = null

  try {
    if (archived) {
      await endpoints.archiveHousehold(groupId.value, household.id)
      notice.value = `${household.name} is archived. Their past meals are unchanged.`
    } else {
      await endpoints.unarchiveHousehold(groupId.value, household.id)
      notice.value = `${household.name} is back in the group.`
    }

    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

/**
 * Creates an invitation for everybody who has none.
 *
 * "Send" is a word this cannot honour yet: there is no outbound SMS, so what
 * this does is mint the links and hand them back to be sent by whatever the
 * organizer already uses to talk to these families (SPEC §6.7.1). The button
 * says so.
 */
const bulk = ref<{ name: string; message: string }[] | null>(null)

async function inviteEverybodyWaiting() {
  busy.value = 'bulk'
  error.value = null

  try {
    const created: { name: string; message: string }[] = []

    for (const household of unconnected.value) {
      const invitation = await endpoints.createHouseholdInvitation(groupId.value, household.id, {
        phone: null,
        forwardMyself: true,
      })

      created.push({ name: household.name, message: invitation.messageText })
    }

    bulk.value = created
    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

async function copyMessage(message: string) {
  try {
    await navigator.clipboard.writeText(message)
  } catch {
    // The message is on screen and selectable; a refused clipboard is not an
    // error worth interrupting for.
  }
}

watch([groupId, includeArchived], load, { immediate: true })
</script>

<template>
  <v-container class="households">
    <header class="households__header">
      <h1 class="households__title">Households</h1>

      <div class="households__actions">
        <v-btn
          v-if="unconnected.length > 0"
          variant="outlined"
          :loading="busy === 'bulk'"
          @click="inviteEverybodyWaiting"
        >
          Create invitations for {{ unconnected.length }} waiting
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          data-testid="new-household"
          @click="openCreate"
        >
          New household
        </v-btn>
      </div>
    </header>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="notice" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>

    <v-card v-if="bulk" variant="outlined" class="mb-6">
      <v-card-item>
        <v-card-title tag="h2" class="households__bulk-title">
          {{ bulk.length }} invitation{{ bulk.length === 1 ? '' : 's' }} ready to send
        </v-card-title>
        <v-card-subtitle class="households__bulk-subtitle">
          Each one is shown once. Send them now — you can always create another.
        </v-card-subtitle>
      </v-card-item>

      <v-card-text>
        <div v-for="item in bulk" :key="item.name" class="households__bulk-row">
          <div>
            <strong>{{ item.name }}</strong>
            <p class="households__bulk-message">{{ item.message }}</p>
          </div>
          <v-btn variant="text" size="small" @click="copyMessage(item.message)">Copy</v-btn>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="bulk = null">Done</v-btn>
      </v-card-actions>
    </v-card>

    <v-checkbox
      v-model="includeArchived"
      label="Show archived households"
      density="compact"
      hide-details
      class="mb-2"
    />

    <v-skeleton-loader v-if="loading" type="table" />

    <!--
      The empty state points at the one thing to do next (SPEC §8.10), and only
      when the list really is empty. Showing it after a failed load says "you
      have no households" when what happened is that we could not find out.
    -->
    <EmptyState
      v-else-if="!error && households.length === 0"
      icon="mdi-home-heart"
      headline="No households yet"
      description="A household is one family answering together — one RSVP, one set of headcounts, one link. Add the first one and you can send them an invitation straight away."
    >
      <template #action>
        <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openCreate">
          Add the first household
        </v-btn>
      </template>
    </EmptyState>

    <!-- Cards on a phone. -->
    <div v-else-if="isMobile" class="households__cards">
      <v-card
        v-for="household in households"
        :key="household.id"
        variant="outlined"
        class="households__card"
      >
        <v-card-item>
          <v-card-title tag="h2" class="households__name">
            <router-link
              :to="{ name: 'organizer-household', params: { groupId, householdId: household.id } }"
            >
              {{ household.name }}
            </router-link>
          </v-card-title>
          <v-card-subtitle>
            {{ household.defaultAdultCount }} adults &middot; {{ household.defaultChildCount }} kids
          </v-card-subtitle>
        </v-card-item>

        <v-card-text>
          <v-chip
            :color="
              household.isConnected
                ? 'success'
                : household.isAwaitingInvitation
                  ? 'warning'
                  : undefined
            "
            size="small"
            variant="tonal"
          >
            {{
              household.isConnected
                ? `${household.contactCount} connected`
                : household.isAwaitingInvitation
                  ? 'Invitation sent'
                  : 'Not connected'
            }}
          </v-chip>
          <v-chip v-if="household.status === 'Archived'" size="small" variant="tonal" class="ml-2">
            Archived
          </v-chip>
        </v-card-text>

        <v-card-actions>
          <v-btn
            variant="text"
            size="small"
            :to="{ name: 'organizer-household', params: { groupId, householdId: household.id } }"
          >
            Open
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            :loading="busy === household.id"
            @click="copyLink(household)"
          >
            {{ copiedLinkFor === household.id ? 'Copied' : 'Copy link' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- A table on a desktop, from the same list. -->
    <v-table v-else class="households__table">
      <thead>
        <tr>
          <th>Household</th>
          <th>Adults</th>
          <th>Kids</th>
          <th>Connected</th>
          <th><span class="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="household in households" :key="household.id">
          <td>
            <router-link
              :to="{ name: 'organizer-household', params: { groupId, householdId: household.id } }"
            >
              {{ household.name }}
            </router-link>
            <v-chip
              v-if="household.status === 'Archived'"
              size="x-small"
              variant="tonal"
              class="ml-2"
            >
              Archived
            </v-chip>
          </td>
          <td>{{ household.defaultAdultCount }}</td>
          <td>{{ household.defaultChildCount }}</td>
          <td>
            <v-chip
              :color="
                household.isConnected
                  ? 'success'
                  : household.isAwaitingInvitation
                    ? 'warning'
                    : undefined
              "
              size="small"
              variant="tonal"
            >
              {{
                household.isConnected
                  ? `${household.contactCount} connected`
                  : household.isAwaitingInvitation
                    ? 'Invitation sent'
                    : 'Not connected'
              }}
            </v-chip>
          </td>
          <td class="households__actions">
            <v-btn
              variant="text"
              size="small"
              :to="{ name: 'organizer-household', params: { groupId, householdId: household.id } }"
            >
              Open
            </v-btn>
            <v-btn
              variant="text"
              size="small"
              :loading="busy === household.id"
              @click="copyLink(household)"
            >
              {{ copiedLinkFor === household.id ? 'Copied' : 'Copy link' }}
            </v-btn>
            <v-btn
              v-if="household.status === 'Active'"
              variant="text"
              size="small"
              :disabled="busy !== null"
              @click="setArchived(household, true)"
            >
              Archive
            </v-btn>
            <v-btn
              v-else
              variant="text"
              size="small"
              :disabled="busy !== null"
              @click="setArchived(household, false)"
            >
              Reinstate
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>

    <!--
      Name, sizes and notes in one step. Everything here is editable afterwards
      on the household's own screen, so nothing asked for now is a commitment —
      which is what lets this stay four fields instead of a wizard.
    -->
    <v-dialog v-model="creating" max-width="26rem">
      <v-card>
        <v-card-title tag="h2" class="text-h6">New household</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="newName"
            label="Family name"
            placeholder="The Smiths"
            autofocus
            :error-messages="nameError ? [nameError] : []"
            data-testid="new-household-name"
            @keyup.enter="create"
          />

          <div class="households__counts">
            <v-number-input
              v-model="newAdults"
              label="Adults"
              :min="0"
              :max="50"
              control-variant="stacked"
              data-testid="new-household-adults"
            />
            <v-number-input
              v-model="newChildren"
              label="Children"
              :min="0"
              :max="50"
              control-variant="stacked"
              data-testid="new-household-children"
            />
          </div>

          <v-textarea
            v-model="newNotes"
            label="Anything we should know?"
            placeholder="Nut allergy, vegetarian…"
            rows="2"
            auto-grow
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="busy === 'create'" @click="creating = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="busy === 'create'"
            data-testid="new-household-save"
            @click="create"
          >
            Add household
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.households__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.households__counts {
  display: flex;
  gap: 0.75rem;
}

.households__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.households__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.households__name {
  font-size: 1.125rem;
  font-weight: 600;
}

.households__cards {
  display: grid;
  gap: 1rem;
}

.households__actions {
  white-space: nowrap;
  text-align: right;
}

.households__bulk-title {
  font-size: 1.125rem;
  font-weight: 600;
}

.households__bulk-subtitle {
  white-space: normal;
  opacity: 1;
}

.households__bulk-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
}

.households__bulk-row + .households__bulk-row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.households__bulk-message {
  margin: 0.25rem 0 0;
  font-size: 0.9375rem;
  overflow-wrap: anywhere;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
